"use client"

import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

import { coreTextLimits } from "@ibada/shared-types/common"
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Form,
  FormControlledField,
  HelperMessage,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
  Select,
  Switch,
  Textarea,
  cn,
} from "@ibada/ui"

import { useApplyApiFieldErrors } from "@/shared/lib/api-errors/api-form-errors"
import { getApiFormErrorDetails } from "@/shared/lib/api-errors/form-api-errors"
import { useTranslations } from "@/i18n"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { MobileDetailScreen } from "@/shared/components/mobile-detail-screen"
import {
  MobileFormAmountRow,
  MobileFormDateRow,
  MobileFormError,
  MobileFormInputRow,
  MobileFormSection,
  MobileFormSelectRow,
  MobileFormTextArea,
} from "@/shared/components/mobile-form"
import { CheckIcon, XIcon } from "lucide-react"
import { useModulePermission } from "@/features/auth/hooks/use-module-permission"
import { WorkspaceAccessModule } from "@/features/auth/model/auth-types"
import {
  CREATE_OVERLAY_FLAT_FIELDS_CLASS,
  CreateOverlayChoice,
  OptionalLabel,
} from "@/features/create-overlay/components/create-overlay-layout"
import { FundCreatePreview, type FundPreviewDraft } from "@/features/finance/components/funds/fund-create-preview"
import { useFundSpendingSummary } from "@/features/finance/components/funds/fund-spending-summary"
import { InviteBranchMultiSelect } from "@/features/users/components/invite/invite-branch-multi-select"

import { AccountingAccountType } from "@/features/finance/model/accounting-reports-types"
import {
  FinanceActivityStatus,
  FinanceCategoryScope,
  FinanceDirectoryStatusFilter,
  FinanceFundKind,
  type FinanceFundRecord,
} from "@/features/finance/model/finance-types"
import { getCurrencyPrefix } from "@/features/finance/lib/finance-utils"
import {
  buildFinanceFundFormSchema,
  createDefaultFinanceFundFormValues,
  FundSpendingMode,
  type FinanceFundFormInput,
  type FinanceFundFormValues,
} from "@/features/finance/schemas/finance-configuration-forms"
import {
  useAccountingChartQuery,
  useFinanceActivitiesQuery,
  useFinanceAttachFundActivitiesMutation,
  useFinanceCreateFundMutation,
  useFinanceSetFundDedicationMutation,
  useFinanceUpdateFundMutation,
} from "@/features/finance/services/finance-query"

/** The desktop form's id: the footer's save button sits outside it (`<button form>`), as in the create overlay. */
const FUND_FORM_ID = "finance-fund-form"

/** Seeds the edit form from a saved fund. */
export function mapFundToFormValues(record: FinanceFundRecord | null): FinanceFundFormValues {
  if (!record) {
    return createDefaultFinanceFundFormValues()
  }

  return {
    // The field's VALUE, not a label: the form submits the stored name back. Only CUSTOM funds are editable
    // and they have no translation key, so there is nothing to resolve here anyway.
    name: record.name,
    description: record.description ?? "",
    // The API sends the target as a canonical decimal string; it seeds the field unchanged (IBA-116).
    targetAmount: record.targetAmount ?? "",
    startDate: record.startDate ? record.startDate.slice(0, 10) : "",
    endDate: record.endDate ? record.endDate.slice(0, 10) : "",
    spendingMode: record.isRestricted ? FundSpendingMode.SelectedExpenses : FundSpendingMode.AnyExpense,
    allowedExpenseAccountIds: record.allowedExpenseAccountIds,
    // A saved period opens with its switch on.
    hasRestrictionPeriod: Boolean(record.restrictionStartDate || record.restrictionEndDate),
    restrictionStartDate: record.restrictionStartDate ? record.restrictionStartDate.slice(0, 10) : "",
    restrictionEndDate: record.restrictionEndDate ? record.restrictionEndDate.slice(0, 10) : "",
  }
}

/** Turns validated form values into the create/update payload. Money stays a string — a goal is money. */
export function toFundPayload(values: FinanceFundFormInput) {
  const isRestricted = values.spendingMode === FundSpendingMode.SelectedExpenses
  // The period counts only while its switch is on: turning it off drops the dates rather than saving them unseen.
  const withPeriod = isRestricted && values.hasRestrictionPeriod
  return {
    name: values.name.trim(),
    description: values.description.trim().length > 0 ? values.description.trim() : null,
    // Already canonical money (or `""` for "no goal"), sent as the string it is. `Number(trimmed)` is what this
    // replaces — a fund's goal is money, and money does not survive a float.
    targetAmount: values.targetAmount === "" ? null : values.targetAmount,
    startDate: values.startDate.trim().length > 0 ? values.startDate.trim() : null,
    endDate: values.endDate.trim().length > 0 ? values.endDate.trim() : null,
    // Restricted Funds: only meaningful when restricted; send the config so the backend can enforce it.
    isRestricted,
    allowedExpenseAccountIds: isRestricted ? values.allowedExpenseAccountIds : [],
    restrictionStartDate: withPeriod && values.restrictionStartDate.trim().length > 0 ? values.restrictionStartDate.trim() : null,
    restrictionEndDate: withPeriod && values.restrictionEndDate.trim().length > 0 ? values.restrictionEndDate.trim() : null,
  }
}

type FundFormDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: "create" | "edit"
  /** The fund being edited. Ignored in create mode. */
  record?: FinanceFundRecord | null
  workspaceId: string | null
  branchId: string | null
  scope: FinanceCategoryScope
  baseCurrency: string
  /** Create mode: prefill the name (e.g. the activity a fund is being created for). */
  defaultName?: string
  /** Create mode: dedicate the new fund to this activity in the same call (IBA-175). */
  dedicateToActivityId?: string
  /** Shown on the locked activity field when `dedicateToActivityId` is set. */
  dedicateToActivityName?: string
  onSaved?: (fund: FinanceFundRecord) => void
}

/**
 * The one fund create/edit dialog.
 *
 * Lifted out of the funds page so the activity settings card opens the SAME form rather than a reduced
 * lookalike. A second dialog asking only for a name would quietly deny a target, a period and the spending
 * restrictions to anyone who happened to create their fund from an activity.
 */
export function FundFormDialog({
  open,
  onOpenChange,
  mode,
  record = null,
  workspaceId,
  branchId,
  scope,
  baseCurrency,
  defaultName,
  dedicateToActivityId,
  dedicateToActivityName,
  onSaved,
}: FundFormDialogProps) {
  const t = useTranslations("finance.funds")
  const { canEdit } = useModulePermission(WorkspaceAccessModule.FINANCE)
  const applyFieldErrors = useApplyApiFieldErrors<FinanceFundFormInput>("finance")

  const schema = React.useMemo(
    () => buildFinanceFundFormSchema((key, values) => t(key as never, values as never), baseCurrency),
    [baseCurrency, t],
  )

  const form = useForm<FinanceFundFormValues, undefined, FinanceFundFormInput>({
    resolver: zodResolver(schema),
    defaultValues: createDefaultFinanceFundFormValues(),
  })

  const createFundMutation = useFinanceCreateFundMutation()
  const updateFundMutation = useFinanceUpdateFundMutation()
  const attachActivitiesMutation = useFinanceAttachFundActivitiesMutation()
  const setDedicationMutation = useFinanceSetFundDedicationMutation()
  const isPending = createFundMutation.isPending || updateFundMutation.isPending || setDedicationMutation.isPending
  const isEditUnchanged = Boolean(mode === "edit" && !form.formState.isDirty)

  const [createActivityIds, setCreateActivityIds] = React.useState<string[]>([])
  /*
   * The dedication (IBA-175). Distinct from the Activities field above and stricter: that one lists the
   * activities which DRAW from this fund, any number of them. This one says the fund may only ever move
   * against a single activity, and the API refuses any transaction naming another.
   */
  const [dedicatedActivityId, setDedicatedActivityId] = React.useState("")

  // Reseed on every open, so a previous edit never leaks into the next one.
  React.useEffect(() => {
    if (!open) return
    setCreateActivityIds([])
    setDedicatedActivityId(
      dedicateToActivityId ?? (mode === "edit" ? (record?.dedicatedActivityId ?? "") : ""),
    )
    form.clearErrors()
    const seeded = mapFundToFormValues(mode === "edit" ? record : null)
    form.reset(defaultName && mode === "create" ? { ...seeded, name: defaultName } : seeded)
  }, [open, mode, record, defaultName, dedicateToActivityId, form])

  const createActivitiesQuery = useFinanceActivitiesQuery(
    open && workspaceId
      ? { workspaceId, branchId: branchId ?? undefined, status: FinanceActivityStatus.Active }
      : null,
  )
  const createActivityOptions = React.useMemo(
    () => (createActivitiesQuery.data ?? []).map((activity) => ({ value: activity.id, label: activity.title })),
    [createActivitiesQuery.data],
  )

  // categories→accounts: a restricted fund's allow-list is EXPENSE AccountingAccounts, listed from the Chart of
  // Accounts (type EXPENSE), shown with their code as a prefix ("6xx Name").
  const restrictionAccountsQuery = useAccountingChartQuery(
    open && workspaceId && branchId ? { workspaceId, branchId, scope, type: AccountingAccountType.Expense } : null,
  )
  const restrictionAccountOptions = React.useMemo(
    () =>
      (restrictionAccountsQuery.data?.items ?? [])
        .filter((account) => account.status === FinanceDirectoryStatusFilter.Active)
        .map((account) => {
          // Localized by the API (ADR-0030). The name alone: account codes are accounting jargon here.
          return { value: account.id, label: account.name }
        }),
    [restrictionAccountsQuery.data?.items],
  )

  /*
   * Choosing a dedication hides the spending-restriction section, so anything already configured there has
   * to be cleared rather than submitted invisibly — otherwise a user who toggled restrictions on and then
   * dedicated the fund would save a rule they can no longer see, let alone undo.
   */
  React.useEffect(() => {
    if (!dedicatedActivityId) return
    if (form.getValues("spendingMode") !== FundSpendingMode.SelectedExpenses) return
    form.setValue("spendingMode", FundSpendingMode.AnyExpense, { shouldDirty: true })
    form.setValue("allowedExpenseAccountIds", [], { shouldDirty: true })
    form.setValue("hasRestrictionPeriod", false, { shouldDirty: true })
    form.setValue("restrictionStartDate", "", { shouldDirty: true })
    form.setValue("restrictionEndDate", "", { shouldDirty: true })
  }, [dedicatedActivityId, form])

  // Opened from an activity, the picker shows that one and nothing else — the activity list has not loaded
  // yet on first paint, and an empty picker beside a fixed value reads as a bug.
  const dedicatedOptions = dedicateToActivityId
    ? [{ value: dedicateToActivityId, label: dedicateToActivityName ?? dedicateToActivityId }]
    : null

  const handleSubmit = form.handleSubmit(async (values) => {
    if (!canEdit) return
    if (!workspaceId) {
      form.setError("root", { message: t("validation.workspaceRequired") })
      return
    }
    if (scope === FinanceCategoryScope.Branch && !branchId) {
      form.setError("root", { message: t("validation.branchRequired") })
      return
    }

    form.clearErrors("root")

    try {
      const payload = toFundPayload(values)
      let saved: FinanceFundRecord

      if (mode === "edit" && record) {
        saved = await updateFundMutation.mutateAsync({ id: record.id, patch: payload })
        // Only when it actually changed, so saving an unrelated edit never rewrites the restriction.
        if ((record.dedicatedActivityId ?? "") !== dedicatedActivityId) {
          saved = await setDedicationMutation.mutateAsync({
            id: record.id,
            activityId: dedicatedActivityId || null,
          })
        }
      } else {
        saved = await createFundMutation.mutateAsync({
          workspaceId,
          branchId: branchId ?? undefined,
          scope,
          ...payload,
          // Dedicating in the same call leaves no window where the fund exists unattached (IBA-175).
          ...(dedicatedActivityId ? { dedicateToActivityId: dedicatedActivityId } : {}),
        })
        /*
         * Attach the activities that draw from this fund. A dedicated activity is attached too: the
         * dedication says what the fund MAY do, the attachment says what the activity PREFERS, and an
         * activity whose own fund was left unset would not pre-select the very fund created for it.
         */
        const toAttach = Array.from(new Set([...createActivityIds, ...(dedicatedActivityId ? [dedicatedActivityId] : [])]))
        if (toAttach.length > 0) {
          await attachActivitiesMutation.mutateAsync({ id: saved.id, activityIds: toAttach })
        }
      }

      toast.success(mode === "edit" ? t("toasts.updated") : t("toasts.created"))
      onSaved?.(saved)
      onOpenChange(false)
    } catch (error) {
      applyFieldErrors(form.setError, error)
      const details = getApiFormErrorDetails(error)
      if (details.formErrors.length > 0) {
        form.setError("root", { message: t("toasts.saveError") })
      }
    }
  })

  const isMobile = useIsMobile()
  const tOverlay = useTranslations("createOverlay")

  // The live preview reads the whole form (`watch`), with ids already resolved to the names the pickers show.
  const watched = form.watch()
  const spendingSummaryFor = useFundSpendingSummary()
  const isSelectedSpending = watched.spendingMode === FundSpendingMode.SelectedExpenses
  const periodOn = isSelectedSpending && Boolean(watched.hasRestrictionPeriod)
  const allowedIds = new Set(watched.allowedExpenseAccountIds ?? [])
  const dedicatedActivityName = dedicatedActivityId
    ? ((dedicatedOptions ?? createActivityOptions).find((option) => option.value === dedicatedActivityId)?.label ??
      (record?.dedicatedActivityId === dedicatedActivityId ? record.dedicatedActivityName : null) ??
      null)
    : null
  const spendingSummary = spendingSummaryFor({
    mode: watched.spendingMode ?? FundSpendingMode.AnyExpense,
    categoryNames: restrictionAccountOptions.filter((option) => allowedIds.has(option.value)).map((option) => option.label),
    periodStart: periodOn ? (watched.restrictionStartDate ?? "") : "",
    periodEnd: periodOn ? (watched.restrictionEndDate ?? "") : "",
    dedicatedActivityName,
  })
  const previewDraft: FundPreviewDraft = {
    name: watched.name ?? "",
    description: watched.description ?? "",
    targetAmount: watched.targetAmount ?? "",
    startDate: watched.startDate ?? "",
    endDate: watched.endDate ?? "",
    isRestricted: isSelectedSpending,
    restrictionStartDate: periodOn ? (watched.restrictionStartDate ?? "") : "",
    restrictionEndDate: periodOn ? (watched.restrictionEndDate ?? "") : "",
    dedicatedActivityName,
    activityNames: createActivityOptions.filter((option) => createActivityIds.includes(option.value)).map((option) => option.label),
  }
  const multiSelectLabels = {
    placeholder: t("spending.categoriesPlaceholder"),
    search: t("spending.search"),
    empty: t("spending.empty"),
    selectAll: t("spending.selectAll"),
    clear: t("spending.clear"),
    summary: (names: string, count: number) => t("spending.more", { names, count }),
  }
  const setSpendingMode = (mode: FundSpendingMode) =>
    form.setValue("spendingMode", mode, { shouldDirty: true, shouldValidate: form.formState.isSubmitted })

  if (!canEdit) return null

  // Phone: a full-screen form (Annuler / title / Enregistrer) of grouped rows. Full screen rather than a sheet
  // because its activity picker and calendars open as sheets of their own, and a sheet never opens on a sheet.
  if (isMobile) {
    const values = watched
    const errors = form.formState.errors
    const set = <K extends keyof FinanceFundFormValues>(name: K, value: FinanceFundFormValues[K]) =>
      form.setValue(name, value as never, { shouldDirty: true, shouldValidate: form.formState.isSubmitted })
    const allowed = new Set<string>(values.allowedExpenseAccountIds ?? [])
    const checkRow = (key: string, label: string, checked: boolean, toggle: () => void) => (
      <button
        key={key}
        type='button'
        role='checkbox'
        aria-checked={checked}
        onClick={toggle}
        className='flex min-h-[3.25rem] w-full items-center gap-3 px-4 py-2 text-left active:bg-muted'
      >
        <span className='line-clamp-2 min-w-0 flex-1 text-base font-semibold break-words'>{label}</span>
        {checked ? <CheckIcon aria-hidden className='size-5 shrink-0 text-primary' strokeWidth={3} /> : null}
      </button>
    )
    const picker = { doneLabel: t("mobile.done"), emptyLabel: t("mobile.noResults") }
    return (
      <MobileDetailScreen
        open={open}
        onOpenChange={onOpenChange}
        title={mode === "edit" ? t("dialog.editTitle") : t("dialog.createTitle")}
        backLabel={t("actions.cancel")}
        showBackChevron={false}
        action={{
          label: mode === "edit" ? t("actions.save") : t("actions.create"),
          onPress: () => void handleSubmit(),
          disabled: isPending || isEditUnchanged,
        }}
      >
        {errors.root?.message ? <HelperMessage tone='danger'>{errors.root.message}</HelperMessage> : null}

        <div>
          <MobileFormSection>
            <MobileFormInputRow
              label={t("fields.name")}
              value={values.name ?? ""}
              onChange={(event) => set("name", event.target.value)}
              placeholder={t("fields.namePlaceholder")}
              maxLength={coreTextLimits.financeAccountName}
              invalid={Boolean(errors.name)}
            />
            <MobileFormAmountRow
              label={t("fields.targetAmount")}
              value={values.targetAmount ?? ""}
              onChange={(value) => set("targetAmount", value)}
              currencyCode={baseCurrency}
              invalid={Boolean(errors.targetAmount)}
            />
          </MobileFormSection>
          <MobileFormError message={errors.name?.message ?? errors.targetAmount?.message} />
        </div>

        <div>
          <MobileFormSection>
            <MobileFormDateRow
              label={t("fields.startDate")}
              value={values.startDate ?? ""}
              onChange={(value) => set("startDate", value)}
              placeholder={t("mobile.none")}
              clearLabel={t("mobile.clearDate")}
            />
            <MobileFormDateRow
              label={t("fields.endDate")}
              value={values.endDate ?? ""}
              onChange={(value) => set("endDate", value)}
              placeholder={t("mobile.none")}
              clearLabel={t("mobile.clearDate")}
              invalid={Boolean(errors.endDate)}
            />
          </MobileFormSection>
          <MobileFormError message={errors.startDate?.message ?? errors.endDate?.message} />
        </div>

        <MobileFormSection title={t("fields.description")}>
          <MobileFormTextArea
            value={values.description ?? ""}
            onChange={(event) => set("description", event.target.value)}
            placeholder={t("fields.descriptionPlaceholder")}
            maxLength={coreTextLimits.financeDescription}
            aria-label={t("fields.description")}
          />
        </MobileFormSection>

        {record?.kind === FinanceFundKind.SystemLinked ? null : (
          <MobileFormSection footer={t("fields.dedicatedActivityHint")}>
            <MobileFormSelectRow
              {...picker}
              label={t("fields.dedicatedActivity")}
              placeholder={t("mobile.none")}
              value={dedicatedActivityId || undefined}
              onChange={(value) => setDedicatedActivityId(value ?? "")}
              options={(dedicatedOptions ?? createActivityOptions).map((option) => ({ value: option.value, label: option.label }))}
              searchPlaceholder={t("fields.dedicatedActivity")}
              clearLabel={t("mobile.noActivity")}
              disabled={Boolean(dedicateToActivityId) || createActivitiesQuery.isLoading}
            />
          </MobileFormSection>
        )}

        {!dedicatedActivityId ? (
          <div>
            <MobileFormSection title={t("spending.title")} footer={spendingSummary}>
              <label className='flex min-h-[3.25rem] w-full items-center gap-3 px-4'>
                <span className='min-w-0 flex-1 truncate text-base font-semibold'>{t("spending.modeSelected")}</span>
                <Switch
                  aria-label={t("spending.modeSelected")}
                  checked={isSelectedSpending}
                  onCheckedChange={(value) =>
                    setSpendingMode(value ? FundSpendingMode.SelectedExpenses : FundSpendingMode.AnyExpense)
                  }
                />
              </label>
              {isSelectedSpending ? (
                <>
                  <label className='flex min-h-[3.25rem] w-full items-center gap-3 px-4'>
                    <span className='min-w-0 flex-1 truncate text-base font-semibold'>{t("spending.periodToggle")}</span>
                    <Switch
                      aria-label={t("spending.periodToggle")}
                      checked={periodOn}
                      onCheckedChange={(value) => set("hasRestrictionPeriod", value)}
                    />
                  </label>
                  {periodOn ? (
                    <>
                      <MobileFormDateRow
                        label={t("spending.periodStart")}
                        value={values.restrictionStartDate ?? ""}
                        onChange={(value) => set("restrictionStartDate", value)}
                        placeholder={t("mobile.none")}
                        clearLabel={t("mobile.clearDate")}
                        invalid={Boolean(errors.restrictionStartDate)}
                      />
                      <MobileFormDateRow
                        label={t("spending.periodEnd")}
                        value={values.restrictionEndDate ?? ""}
                        onChange={(value) => set("restrictionEndDate", value)}
                        placeholder={t("mobile.none")}
                        clearLabel={t("mobile.clearDate")}
                        invalid={Boolean(errors.restrictionEndDate)}
                      />
                    </>
                  ) : null}
                </>
              ) : null}
            </MobileFormSection>
            <MobileFormError message={errors.restrictionStartDate?.message ?? errors.restrictionEndDate?.message} />
            {isSelectedSpending ? (
              <>
                <MobileFormSection className='mt-5' title={t("spending.categories")}>
                  {restrictionAccountOptions.length === 0 ? (
                    <p className='px-4 py-3 text-sm font-semibold text-muted-foreground'>{t("spending.empty")}</p>
                  ) : (
                    restrictionAccountOptions.map((option) =>
                      checkRow(option.value, option.label, allowed.has(option.value), () => {
                        const next = new Set(allowed)
                        if (next.has(option.value)) next.delete(option.value)
                        else next.add(option.value)
                        set("allowedExpenseAccountIds", [...next])
                      }),
                    )
                  )}
                </MobileFormSection>
                <MobileFormError message={errors.allowedExpenseAccountIds?.message} />
              </>
            ) : null}
          </div>
        ) : null}

        {mode === "create" && !dedicatedActivityId && createActivityOptions.length > 0 ? (
          <MobileFormSection title={t("fields.activities")} footer={t("fields.activitiesHint")}>
            {createActivityOptions.map((option) =>
              checkRow(option.value, option.label, createActivityIds.includes(option.value), () =>
                setCreateActivityIds((current) =>
                  current.includes(option.value) ? current.filter((id) => id !== option.value) : [...current, option.value],
                ),
              ),
            )}
          </MobileFormSection>
        ) : null}
      </MobileDetailScreen>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid='finance-funds-dialog'
        showCloseButton={false}
        className={cn(
          // The create overlay's layout C: slim 40px pill fields, the preview panel the full height on the right.
          "[--control-h:2.5rem] [&_[data-slot=dialog-body]]:h-full [&_[data-slot=dialog-body]]:p-0 [&_[data-slot=dialog-scroll-area]]:overflow-hidden",
          "h-[min(48.75rem,calc(100dvh-2rem))] sm:max-w-[62.5rem]",
        )}
      >
        <div className='flex h-full min-h-0'>
          <div className='flex min-w-0 flex-1 flex-col'>
            <div className='flex items-start gap-3 px-7 pt-[22px] pb-4'>
              <div className='flex min-w-0 flex-1 flex-col gap-1'>
                <DialogTitle className='text-[21px] leading-tight font-extrabold tracking-[-0.2px]'>
                  {mode === "edit" ? t("dialog.editTitle") : t("dialog.createTitle")}
                </DialogTitle>
                <DialogDescription className='text-[13.5px]'>{t("dialog.description")}</DialogDescription>
              </div>
              <button
                type='button'
                onClick={() => onOpenChange(false)}
                disabled={isPending}
                aria-label={tOverlay("close")}
                className='bg-muted text-foreground/75 hover:text-foreground focus-visible:ring-ring/50 flex size-9 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:ring-3 focus-visible:outline-none'
              >
                <XIcon className='size-[18px]' />
              </button>
            </div>

        <Form {...form}>
          <form id={FUND_FORM_ID} className='min-h-0 flex-1 overflow-y-auto' onSubmit={handleSubmit} noValidate>
            <div className={cn("flex min-w-0 flex-col gap-4 px-7 pt-1 pb-6", CREATE_OVERLAY_FLAT_FIELDS_CLASS)}>
            {form.formState.errors.root?.message ? (
              <HelperMessage tone='danger'>{form.formState.errors.root.message}</HelperMessage>
            ) : null}

            <FormControlledField
              control={form.control}
              name='name'
              label={t("fields.name")}
              required
              renderInput={({ field }) => (
                <Input
                  id='finance-fund-name'
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("fields.namePlaceholder")}
                  maxLength={coreTextLimits.financeAccountName}
                  showCount
                />
              )}
            />

            <FormControlledField
              control={form.control}
              name='description'
              label={<OptionalLabel>{t("fields.description")}</OptionalLabel>}
              renderInput={({ field }) => (
                <Textarea
                  id='finance-fund-description'
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("fields.descriptionPlaceholder")}
                  maxLength={coreTextLimits.financeDescription}
                  showCount
                />
              )}
            />

            <FormControlledField
              control={form.control}
              name='targetAmount'
              label={<OptionalLabel>{t("fields.targetAmount")}</OptionalLabel>}
              renderInput={({ field }) => (
                <InputGroup>
                  <InputGroupAddon align='inline-start'>
                    <InputGroupText>{getCurrencyPrefix(baseCurrency)}</InputGroupText>
                  </InputGroupAddon>
                  <InputGroupInput
                    id='finance-fund-target-amount'
                    type='number'
                    min={0}
                    step='any'
                    inputMode='decimal'
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("fields.targetAmountPlaceholder")}
                  />
                </InputGroup>
              )}
            />

            <div className='grid gap-4 sm:grid-cols-2'>
              <FormControlledField
                control={form.control}
                name='startDate'
                label={<OptionalLabel>{t("fields.startDate")}</OptionalLabel>}
                renderInput={({ field }) => (
                  <Input
                    id='finance-fund-start-date'
                    type='date'
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("fields.datePlaceholder")}
                  />
                )}
              />

              <FormControlledField
                control={form.control}
                name='endDate'
                label={<OptionalLabel>{t("fields.endDate")}</OptionalLabel>}
                renderInput={({ field }) => (
                  <Input
                    id='finance-fund-end-date'
                    type='date'
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("fields.datePlaceholder")}
                  />
                )}
              />
            </div>

            {/*
              A SYSTEM_LINKED fund mirrors an income account and collects everything posted to it across the
              whole workspace, so it cannot be ring-fenced to one activity — that would strand every other
              activity's money in it. The API refuses; the field is simply absent rather than offered.
            */}
            {record?.kind === FinanceFundKind.SystemLinked ? null : (
            <div className='space-y-1.5'>
              <p className='text-foreground text-[13px] font-bold'>
                <OptionalLabel>{t("fields.dedicatedActivity")}</OptionalLabel>
              </p>
              <Select
                isSearchable
                isClearable
                options={dedicatedOptions ?? createActivityOptions}
                value={dedicatedActivityId}
                onValueChange={(value) => setDedicatedActivityId(value ?? "")}
                placeholder={t("fields.dedicatedActivityPlaceholder")}
                // Fixed when the dialog was opened FROM an activity — that is the whole reason it is open.
                disabled={Boolean(dedicateToActivityId) || createActivitiesQuery.isLoading}
              />
              <p className='text-muted-foreground text-xs'>{t("fields.dedicatedActivityHint")}</p>
            </div>
            )}

            {/*
              How this money can be spent: every expense, or only some (chosen expense accounts and/or a period).
              Hidden once a dedication is chosen: a fund tied to one activity is already restricted by that, and a
              second, looser rule beneath it only invites a contradictory answer.
            */}
            {!dedicatedActivityId ? (
              <section className='flex flex-col gap-3' data-testid='fund-spending-section'>
                <h3 className='text-foreground text-[13.5px] font-extrabold'>{t("spending.title")}</h3>
                <CreateOverlayChoice
                  ariaLabel={t("spending.title")}
                  value={watched.spendingMode}
                  onChange={setSpendingMode}
                  options={[
                    { value: FundSpendingMode.AnyExpense, label: t("spending.modeAny") },
                    { value: FundSpendingMode.SelectedExpenses, label: t("spending.modeSelected") },
                  ]}
                />

                {isSelectedSpending ? (
                  <>
                    <FormControlledField
                      control={form.control}
                      name='allowedExpenseAccountIds'
                      label={t("spending.categories")}
                      renderInput={({ field, fieldState }) => (
                        <InviteBranchMultiSelect
                          options={restrictionAccountOptions}
                          value={field.value ?? []}
                          onChange={(next) => field.onChange(next)}
                          labels={multiSelectLabels}
                          isMobile={false}
                          title={t("spending.categories")}
                          invalid={Boolean(fieldState.error)}
                          disabled={restrictionAccountsQuery.isLoading}
                        />
                      )}
                    />

                    <label className='flex cursor-pointer items-center justify-between gap-3'>
                      <span className='text-foreground text-[13px] font-bold'>{t("spending.periodToggle")}</span>
                      <Switch
                        aria-label={t("spending.periodToggle")}
                        checked={periodOn}
                        onCheckedChange={(value) =>
                          form.setValue("hasRestrictionPeriod", value, {
                            shouldDirty: true,
                            shouldValidate: form.formState.isSubmitted,
                          })
                        }
                      />
                    </label>

                    {periodOn ? (
                      <div className='grid gap-4 sm:grid-cols-2'>
                        <FormControlledField
                          control={form.control}
                          name='restrictionStartDate'
                          label={t("spending.periodStart")}
                          renderInput={({ field }) => (
                            <Input
                              type='date'
                              value={field.value}
                              onChange={field.onChange}
                              placeholder={t("fields.datePlaceholder")}
                            />
                          )}
                        />
                        <FormControlledField
                          control={form.control}
                          name='restrictionEndDate'
                          label={t("spending.periodEnd")}
                          renderInput={({ field }) => (
                            <Input
                              type='date'
                              value={field.value}
                              onChange={field.onChange}
                              placeholder={t("fields.datePlaceholder")}
                            />
                          )}
                        />
                      </div>
                    ) : null}
                  </>
                ) : null}

                <p className='text-muted-foreground text-xs leading-snug' data-testid='fund-spending-summary'>
                  {spendingSummary}
                </p>
              </section>
            ) : null}


            {/*
              Which activities DRAW from this fund — many of them, and a preference, not a rule.
              Hidden once a dedication is chosen, whether by the user just now or by opening the dialog from
              an activity: a fund that may only move against one activity has nothing left to ask here, and
              a second list showing the same name only invites the reader to wonder how the two differ. The
              dedicated activity is attached on save regardless, so nothing is lost by not asking.
            */}
            {mode === "create" && !dedicatedActivityId ? (
              <div className='space-y-1.5'>
                <p className='text-foreground text-[13px] font-bold'>
                  <OptionalLabel>{t("fields.activities")}</OptionalLabel>
                </p>
                <Select
                  isMulti
                  isSearchable
                  options={createActivityOptions}
                  value={createActivityIds}
                  onValueChange={(value) => setCreateActivityIds(value)}
                  placeholder={t("attach.placeholder")}
                  disabled={createActivitiesQuery.isLoading}
                />
                <p className='text-muted-foreground text-xs'>{t("fields.activitiesHint")}</p>
              </div>
            ) : null}

            </div>
          </form>
        </Form>
          </div>

          <aside
            data-testid='finance-funds-preview-panel'
            className='border-border/50 flex w-[18.75rem] shrink-0 flex-col gap-4 overflow-y-auto border-l bg-[var(--app-canvas)] p-6'
          >
            <span className='text-muted-foreground text-xs font-extrabold'>{tOverlay("preview")}</span>
            <FundCreatePreview
              draft={previewDraft}
              currency={baseCurrency}
              scope={scope}
              spendingSummary={spendingSummary}
              record={mode === "edit" ? record : null}
            />
          </aside>
        </div>

        <DialogFooter className='justify-end gap-2.5 px-7 py-4'>
          <Button
            type='button'
            variant='secondary'
            className='bg-muted hover:bg-muted/80 border-0 px-5 font-bold shadow-none'
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t("actions.cancel")}
          </Button>
          <Button
            type='submit'
            form={FUND_FORM_ID}
            className='px-5 font-extrabold disabled:opacity-70'
            loading={isPending}
            disabled={isPending || isEditUnchanged}
          >
            {mode === "edit" ? t("actions.save") : t("actions.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
